import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Eye, EyeOff, Mail, Lock, User, Phone, MapPin, Briefcase } from 'lucide-react';
import GoogleAuth from '../components/GoogleAuth';
import toast from 'react-hot-toast';

const Register = () => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    password: '',
    confirmPassword: '',
    dateOfBirth: '',
    address: '',
    city: '',
    state: '',
    bvn: '',
    bankAccountNumber: '',
    bankName: '',
    accountName: '',
    employmentStatus: '',
    monthlyIncome: '',
    employerName: '',
    jobTitle: '',
    employmentDuration: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  
  const { register, user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    // Only redirect if user is already logged in (not during registration)
    // This prevents redirect during the registration process
    if (user && !isLoading) {
      // Role-based redirect - same as login
      const userRole = user?.role || 'user';
      if (userRole === 'admin') {
        navigate('/admin', { replace: true });
      } else {
        navigate('/dashboard', { replace: true });
      }
    }
  }, [user, navigate, isLoading]);

  const formatNigerianPhone = (value) => {
    // Remove all non-digit characters
    let digits = value.replace(/\D/g, '');
    
    // If starts with 234, keep it
    if (digits.startsWith('234')) {
      // Format as +234 XXX XXX XXXX
      if (digits.length <= 3) return `+${digits}`;
      if (digits.length <= 6) return `+${digits.slice(0, 3)} ${digits.slice(3)}`;
      if (digits.length <= 9) return `+${digits.slice(0, 3)} ${digits.slice(3, 6)} ${digits.slice(6)}`;
      return `+${digits.slice(0, 3)} ${digits.slice(3, 6)} ${digits.slice(6, 9)} ${digits.slice(9, 13)}`;
    }
    
    // If starts with 0, format as 0XXX XXX XXXX
    if (digits.startsWith('0')) {
      if (digits.length <= 4) return digits;
      if (digits.length <= 7) return `${digits.slice(0, 4)} ${digits.slice(4)}`;
      return `${digits.slice(0, 4)} ${digits.slice(4, 7)} ${digits.slice(7, 11)}`;
    }
    
    // If just digits, assume it's a local number starting with 0
    if (digits.length > 0 && !digits.startsWith('0') && !digits.startsWith('234')) {
      digits = '0' + digits;
    }
    
    if (digits.length <= 4) return digits;
    if (digits.length <= 7) return `${digits.slice(0, 4)} ${digits.slice(4)}`;
    return `${digits.slice(0, 4)} ${digits.slice(4, 7)} ${digits.slice(7, 11)}`;
  };

  const validateNigerianPhone = (phone) => {
    // Remove all non-digit characters for validation
    const digits = phone.replace(/\D/g, '');
    
    // Must be either 11 digits (0XXXXXXXXXX) or 13 digits (234XXXXXXXXXX)
    if (digits.length !== 11 && digits.length !== 13) {
      return false;
    }
    
    // If 11 digits, must start with 0
    if (digits.length === 11 && !digits.startsWith('0')) {
      return false;
    }
    
    // If 13 digits, must start with 234
    if (digits.length === 13 && !digits.startsWith('234')) {
      return false;
    }
    
    // Valid Nigerian mobile prefixes (all major networks)
    const validPrefixes = ['070', '080', '081', '090', '091', '082', '083', '084', '085', '086', '087', '088', '089', '092', '093', '094', '095', '096', '097', '098', '099'];
    const prefix = digits.length === 11 ? digits.slice(0, 3) : digits.slice(3, 6);
    
    return validPrefixes.includes(prefix);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    if (name === 'phone') {
      // Format phone number as user types
      const formatted = formatNigerianPhone(value);
      setFormData({
        ...formData,
        [name]: formatted
      });
    } else if (name === 'bankAccountNumber') {
      // Only allow numbers and limit to 10 digits
      const digitsOnly = value.replace(/\D/g, '');
      if (digitsOnly.length <= 10) {
        setFormData({
          ...formData,
          [name]: digitsOnly
        });
      }
    } else {
      setFormData({
        ...formData,
        [name]: value
      });
    }
  };

  const validateStep1 = () => {
    const { firstName, lastName, email, phone, password, confirmPassword } = formData;
    
    if (!firstName || !lastName || !email || !phone || !password || !confirmPassword) {
      toast.error('Please fill in all required fields');
      return false;
    }
    
    if (!validateNigerianPhone(phone)) {
      toast.error('Please enter a valid Nigerian phone number (e.g., +234 800 000 0000 or 0800 000 0000)');
      return false;
    }
    
    if (password !== confirmPassword) {
      toast.error('Passwords do not match');
      return false;
    }
    
    if (password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return false;
    }
    
    return true;
  };

  const validateStep2 = () => {
    const { dateOfBirth, address, city, state, bvn } = formData;
    
    if (!dateOfBirth || !address || !city || !state) {
      toast.error('Please fill in all required fields');
      return false;
    }
    
    if (bvn && bvn.length !== 11) {
      toast.error('BVN must be 11 digits');
      return false;
    }
    
    return true;
  };

  const handleNext = () => {
    if (currentStep === 1 && validateStep1()) {
      setCurrentStep(2);
    } else if (currentStep === 2 && validateStep2()) {
      setCurrentStep(3);
    }
  };

  const handlePrevious = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const result = await register(formData);
      if (result.success) {
        // Role-based redirect - immediately after registration
        // Don't wait for useEffect, redirect right away
        const userRole = result.user?.role || 'user';
        console.log('Registration successful, redirecting based on role:', userRole);
        
        // Use replace: true to prevent back navigation to register page
        if (userRole === 'admin') {
          navigate('/admin', { replace: true });
        } else {
          navigate('/dashboard', { replace: true });
        }
      }
    } catch (error) {
      toast.error('Registration failed. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const renderStep1 = () => (
    <div className="space-y-6">
      <h3 className="text-lg font-semibold text-gray-900 mb-4">Personal Information</h3>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label htmlFor="firstName" className="block text-sm font-medium text-gray-700 mb-2">
            First Name *
          </label>
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <User className="h-5 w-5 text-gray-400" />
            </div>
            <input
              id="firstName"
              name="firstName"
              type="text"
              required
              value={formData.firstName}
              onChange={handleChange}
              className="input-field pl-10"
              placeholder="Enter your first name"
            />
          </div>
        </div>

        <div>
          <label htmlFor="lastName" className="block text-sm font-medium text-gray-700 mb-2">
            Last Name *
          </label>
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <User className="h-5 w-5 text-gray-400" />
            </div>
            <input
              id="lastName"
              name="lastName"
              type="text"
              required
              value={formData.lastName}
              onChange={handleChange}
              className="input-field pl-10"
              placeholder="Enter your last name"
            />
          </div>
        </div>
      </div>

      <div>
        <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
          Email Address *
        </label>
        <div className="relative">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <Mail className="h-5 w-5 text-gray-400" />
          </div>
          <input
            id="email"
            name="email"
            type="email"
            autoComplete="email"
            required
            value={formData.email}
            onChange={handleChange}
            className="input-field pl-10"
            placeholder="Enter your email"
          />
        </div>
      </div>

      <div>
        <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">
          Phone Number *
        </label>
        <div className="relative">
          <Phone className="absolute left-3 top-[45%] transform -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none" />
          <input
            id="phone"
            name="phone"
            type="tel"
            required
            value={formData.phone}
            onChange={handleChange}
            className="input-field pl-10"
            placeholder="+234 800 000 0000 or 0800 000 0000"
            maxLength={17}
          />
          <p className="text-xs text-gray-500 mt-1">Enter a valid Nigerian phone number</p>
        </div>
      </div>

      <div>
        <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
          Password *
        </label>
        <div className="relative">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <Lock className="h-5 w-5 text-gray-400" />
          </div>
          <input
            id="password"
            name="password"
            type={showPassword ? 'text' : 'password'}
            required
            value={formData.password}
            onChange={handleChange}
            className="input-field pl-10 pr-10"
            placeholder="Create a password"
          />
          <button
            type="button"
            className="absolute inset-y-0 right-0 pr-3 flex items-center"
            onClick={() => setShowPassword(!showPassword)}
          >
            {showPassword ? (
              <EyeOff className="h-5 w-5 text-gray-400 hover:text-gray-600" />
            ) : (
              <Eye className="h-5 w-5 text-gray-400 hover:text-gray-600" />
            )}
          </button>
        </div>
      </div>

      <div>
        <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-2">
          Confirm Password *
        </label>
        <div className="relative">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <Lock className="h-5 w-5 text-gray-400" />
          </div>
          <input
            id="confirmPassword"
            name="confirmPassword"
            type={showConfirmPassword ? 'text' : 'password'}
            required
            value={formData.confirmPassword}
            onChange={handleChange}
            className="input-field pl-10 pr-10"
            placeholder="Confirm your password"
          />
          <button
            type="button"
            className="absolute inset-y-0 right-0 pr-3 flex items-center"
            onClick={() => setShowConfirmPassword(!showConfirmPassword)}
          >
            {showConfirmPassword ? (
              <EyeOff className="h-5 w-5 text-gray-400 hover:text-gray-600" />
            ) : (
              <Eye className="h-5 w-5 text-gray-400 hover:text-gray-600" />
            )}
          </button>
        </div>
      </div>
    </div>
  );

  const renderStep2 = () => (
    <div className="space-y-6">
      <h3 className="text-lg font-semibold text-gray-900 mb-4">Personal Details</h3>
      
      <div>
        <label htmlFor="dateOfBirth" className="block text-sm font-medium text-gray-700 mb-2">
          Date of Birth *
        </label>
        <input
          id="dateOfBirth"
          name="dateOfBirth"
          type="date"
          required
          value={formData.dateOfBirth}
          onChange={handleChange}
          className="input-field"
        />
      </div>

      <div>
        <label htmlFor="address" className="block text-sm font-medium text-gray-700 mb-2">
          Address *
        </label>
        <div className="relative">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <MapPin className="h-5 w-5 text-gray-400" />
          </div>
          <textarea
            id="address"
            name="address"
            required
            rows={3}
            value={formData.address}
            onChange={handleChange}
            className="input-field pl-10"
            placeholder="Enter your full address"
          />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label htmlFor="city" className="block text-sm font-medium text-gray-700 mb-2">
            City *
          </label>
          <input
            id="city"
            name="city"
            type="text"
            required
            value={formData.city}
            onChange={handleChange}
            className="input-field"
            placeholder="Enter your city"
          />
        </div>

        <div>
          <label htmlFor="state" className="block text-sm font-medium text-gray-700 mb-2">
            State *
          </label>
          <select
            id="state"
            name="state"
            required
            value={formData.state}
            onChange={handleChange}
            className="input-field"
          >
            <option value="">Select State</option>
            <option value="Lagos">Lagos</option>
            <option value="Abuja">Abuja</option>
            <option value="Kano">Kano</option>
            <option value="Rivers">Rivers</option>
            <option value="Kaduna">Kaduna</option>
            <option value="Ogun">Ogun</option>
            <option value="Oyo">Oyo</option>
            <option value="Edo">Edo</option>
            <option value="Delta">Delta</option>
            <option value="Plateau">Plateau</option>
          </select>
        </div>
      </div>

      <div>
        <label htmlFor="bvn" className="block text-sm font-medium text-gray-700 mb-2">
          BVN (Bank Verification Number) - Optional
        </label>
        <input
          id="bvn"
          name="bvn"
          type="text"
          maxLength="11"
          value={formData.bvn}
          onChange={handleChange}
          className="input-field"
          placeholder="Enter your BVN (11 digits)"
        />
      </div>
    </div>
  );

  const renderStep3 = () => (
    <div className="space-y-6">
      <h3 className="text-lg font-semibold text-gray-900 mb-4">Banking & Employment Details</h3>
      
      <div>
        <label htmlFor="bankAccountNumber" className="block text-sm font-medium text-gray-700 mb-2">
          Bank Account Number
        </label>
        <input
          id="bankAccountNumber"
          name="bankAccountNumber"
          type="text"
          inputMode="numeric"
          maxLength={10}
          value={formData.bankAccountNumber}
          onChange={handleChange}
          className="input-field"
          placeholder="Enter 10-digit account number"
        />
        {formData.bankAccountNumber && formData.bankAccountNumber.length < 10 && (
          <p className="mt-1 text-xs text-gray-500">
            {formData.bankAccountNumber.length}/10 digits
          </p>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label htmlFor="bankName" className="block text-sm font-medium text-gray-700 mb-2">
            Bank Name
          </label>
          <select
            id="bankName"
            name="bankName"
            value={formData.bankName}
            onChange={handleChange}
            className="input-field"
          >
            <option value="">Select Bank</option>
            <option value="Access Bank">Access Bank</option>
            <option value="Citibank">Citibank</option>
            <option value="Ecobank">Ecobank</option>
            <option value="Fidelity Bank">Fidelity Bank</option>
            <option value="First Bank">First Bank</option>
            <option value="First City Monument Bank">First City Monument Bank</option>
            <option value="GTBank">GTBank</option>
            <option value="Heritage Bank">Heritage Bank</option>
            <option value="Keystone Bank">Keystone Bank</option>
            <option value="Kuda Bank">Kuda Bank</option>
            <option value="Opay">Opay</option>
            <option value="PalmPay">PalmPay</option>
            <option value="Polaris Bank">Polaris Bank</option>
            <option value="Providus Bank">Providus Bank</option>
            <option value="Stanbic IBTC">Stanbic IBTC</option>
            <option value="Standard Chartered">Standard Chartered</option>
            <option value="Sterling Bank">Sterling Bank</option>
            <option value="Union Bank">Union Bank</option>
            <option value="United Bank for Africa">United Bank for Africa</option>
            <option value="Unity Bank">Unity Bank</option>
            <option value="VFD">VFD</option>
            <option value="Wema Bank">Wema Bank</option>
            <option value="Zenith Bank">Zenith Bank</option>
          </select>
        </div>

        <div>
          <label htmlFor="accountName" className="block text-sm font-medium text-gray-700 mb-2">
            Account Name
          </label>
          <input
            id="accountName"
            name="accountName"
            type="text"
            value={formData.accountName}
            onChange={handleChange}
            className="input-field"
            placeholder="Enter account name"
          />
        </div>
      </div>

      <div>
        <label htmlFor="employmentStatus" className="block text-sm font-medium text-gray-700 mb-2">
          Employment Status *
        </label>
        <select
          id="employmentStatus"
          name="employmentStatus"
          required
          value={formData.employmentStatus}
          onChange={handleChange}
          className="input-field"
        >
          <option value="">Select Employment Status</option>
          <option value="employed">Employed</option>
          <option value="self-employed">Self-employed</option>
          <option value="unemployed">Unemployed</option>
          <option value="student">Student</option>
          <option value="retired">Retired</option>
        </select>
      </div>

      {formData.employmentStatus === 'employed' && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label htmlFor="employerName" className="block text-sm font-medium text-gray-700 mb-2">
                Employer Name
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Briefcase className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  id="employerName"
                  name="employerName"
                  type="text"
                  value={formData.employerName}
                  onChange={handleChange}
                  className="input-field pl-10"
                  placeholder="Enter employer name"
                />
              </div>
            </div>

            <div>
              <label htmlFor="jobTitle" className="block text-sm font-medium text-gray-700 mb-2">
                Job Title
              </label>
              <input
                id="jobTitle"
                name="jobTitle"
                type="text"
                value={formData.jobTitle}
                onChange={handleChange}
                className="input-field"
                placeholder="Enter your job title"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label htmlFor="monthlyIncome" className="block text-sm font-medium text-gray-700 mb-2">
                Monthly Income (₦)
              </label>
              <input
                id="monthlyIncome"
                name="monthlyIncome"
                type="number"
                value={formData.monthlyIncome}
                onChange={handleChange}
                className="input-field"
                placeholder="Enter monthly income"
              />
            </div>

            <div>
              <label htmlFor="employmentDuration" className="block text-sm font-medium text-gray-700 mb-2">
                Employment Duration (months)
              </label>
              <input
                id="employmentDuration"
                name="employmentDuration"
                type="number"
                value={formData.employmentDuration}
                onChange={handleChange}
                className="input-field"
                placeholder="Enter duration in months"
              />
            </div>
          </div>
        </>
      )}
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 to-secondary-50 pt-20 pb-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-4xl mx-auto" data-aos="fade-up">
        <div className="text-center mb-8">
          <Link to="/" className="flex items-center justify-center space-x-2 mb-6">
            <div className="w-12 h-12 bg-primary-600 rounded-lg flex items-center justify-center">
              <span className="text-white font-bold text-2xl">BF</span>
            </div>
            <span className="text-2xl font-bold text-gray-900">
              Blue Print Financial
            </span>
          </Link>
          <h2 className="text-3xl font-bold text-gray-900">Create your account</h2>
          <p className="mt-2 text-gray-600">
            Join thousands of satisfied customers across Nigeria
          </p>
        </div>

        <div className="bg-white rounded-xl shadow-lg p-8">
          {/* Progress Steps */}
          <div className="mb-8">
            <div className="flex items-center justify-between">
              {[1, 2, 3].map((step) => (
                <div key={step} className="flex items-center">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold ${
                    currentStep >= step 
                      ? 'bg-primary-600 text-white' 
                      : 'bg-gray-200 text-gray-600'
                  }`}>
                    {step}
                  </div>
                  {step < 3 && (
                    <div className={`w-16 h-1 mx-2 ${
                      currentStep > step ? 'bg-primary-600' : 'bg-gray-200'
                    }`} />
                  )}
                </div>
              ))}
            </div>
            <div className="flex justify-between mt-2 text-sm text-gray-600">
              <span>Personal Info</span>
              <span>Details</span>
              <span>Banking & Employment</span>
            </div>
          </div>

          <form onSubmit={handleSubmit}>
            {currentStep === 1 && renderStep1()}
            {currentStep === 2 && renderStep2()}
            {currentStep === 3 && renderStep3()}

            <div className="flex justify-between mt-8">
              <button
                type="button"
                onClick={handlePrevious}
                disabled={currentStep === 1}
                className={`px-6 py-2 border border-gray-300 rounded-lg text-gray-700 ${
                  currentStep === 1 
                    ? 'opacity-50 cursor-not-allowed' 
                    : 'hover:bg-gray-50'
                }`}
              >
                Previous
              </button>

              {currentStep < 3 ? (
                <button
                  type="button"
                  onClick={handleNext}
                  className="btn-primary"
                >
                  Next
                </button>
              ) : (
                <button
                  type="submit"
                  disabled={isLoading}
                  className="btn-primary flex items-center"
                >
                  {isLoading ? (
                    <>
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                      Creating Account...
                    </>
                  ) : (
                    'Create Account'
                  )}
                </button>
              )}
            </div>
          </form>

          <div className="mt-6">
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-300" />
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-2 bg-white text-gray-500">Or continue with</span>
              </div>
            </div>

            <div className="mt-6">
              <GoogleAuth buttonText="Sign up with Google" className="btn-outline w-full flex items-center justify-center" />
            </div>

            <div className="mt-6 text-center">
              <p className="text-sm text-gray-600">
                Already have an account?{' '}
                <Link to="/login" className="font-medium text-primary-600 hover:text-primary-500">
                  Sign in
                </Link>
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Register;



